import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  deleteProgram,
  fetchPrograms,
  publishProgram,
} from "../../store/ProgramSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import moment from "moment";
import { toast } from "react-toastify";
import DeleteConfirmation from "../ConfirmBox";
import { AllPrograms } from "../../interfaces/Program";
import { Typography } from "@material-tailwind/react";

const ListView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const [sortConfig, setSortConfig] = useState<{
    column: string | null;
    sortOrder: "asc" | "desc";
  }>({ column: null, sortOrder: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [openActionId, setOpenActionId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const itemsPerPage = 8;

  const TABLE_HEAD = [
    { key: "ICON", label: "", sortable: false },
    { key: "TITLE", label: t("adminPrograms.table.title", "TITLE"), sortable: true },
    { key: "STATUS", label: t("adminPrograms.table.status", "STATUS"), sortable: false },
    { key: "TOTAL COUNSELLINGS", label: t("adminPrograms.table.totalCounsellings", "TOTAL COUNSELLINGS"), sortable: true },
    { key: "BOOKED SLOTS", label: t("adminPrograms.table.bookedSlots", "BOOKED SLOTS"), sortable: true },
    { key: "START DATE", label: t("adminPrograms.table.startDate", "START DATE"), sortable: true },
    { key: "COST", label: t("adminPrograms.table.cost", "COST"), sortable: false },
    { key: "ACTIONS", label: t("adminPrograms.table.actions", "ACTIONS"), sortable: false },
  ];

  useEffect(() => {
    dispatch(fetchPrograms({ search: "", limit: itemsPerPage }));
  }, [dispatch]);

  // Close action dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenActionId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data, loading, error } = useSelector(
    (state: RootState) => state.programs
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center flex-1 w-full min-h-[300px] py-16">
        <div className="flex flex-col items-center gap-3">
          <div
            className="border-4 border-solid h-10 w-10 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: "#B4804A", borderTopColor: "transparent" }}
          />
          <span className="text-xs font-semibold text-gray-400">Loading programs...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-500 font-semibold text-sm">
        Error loading programs: {error}
      </div>
    );
  }

  const parsedData = typeof data === "string" ? JSON.parse(data) : data;
  const programsList: AllPrograms[] = parsedData?.programs || [];

  if (!parsedData || programsList.length === 0) {
    return (
      <div className="text-center flex-1 flex flex-col items-center justify-center py-16 px-4">
        <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400 mb-3">
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </div>
        <h4 className="text-base font-bold text-[#1E293B] mb-1">
          {t("adminPrograms.noPrograms", "No programs available")}
        </h4>
        <p className="text-xs text-gray-500 max-w-sm">
          No programs match your search or none have been created yet.
        </p>
      </div>
    );
  }

  const mapColumnToCamelCase = (column: string) => {
    const columnMappings: Record<string, string> = {
      TITLE: "title",
      "TOTAL COUNSELLINGS": "totalCounsellings",
      "BOOKED SLOTS": "maxParticipants",
      "START DATE": "startDate",
      COST: "cost",
    };
    return columnMappings[column];
  };

  const handleSort = (column: string, sortable: boolean) => {
    if (!sortable) return;
    const updatedColumnName = mapColumnToCamelCase(column);
    if (!updatedColumnName) return;

    const sortOrder =
      updatedColumnName === sortConfig.column && sortConfig.sortOrder === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ column: updatedColumnName, sortOrder });
    dispatch(fetchPrograms({ sortBy: `${updatedColumnName}:${sortOrder}`, limit: itemsPerPage }));
  };

  const handlePagination = (page: number, limit: number) => {
    dispatch(fetchPrograms({ page, limit }));
  };

  const handleNextClick = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    handlePagination(nextPage, itemsPerPage);
  };

  const handlePreviousClick = () => {
    if (currentPage > 1) {
      const previousPage = currentPage - 1;
      setCurrentPage(previousPage);
      handlePagination(previousPage, itemsPerPage);
    }
  };

  const handleDelete = (programId: string) => {
    dispatch(deleteProgram(programId)).then(() => {
      dispatch(fetchPrograms({ search: "", limit: itemsPerPage })).then(() => {
        toast.success(t("adminPrograms.deleteSuccess", "Successfully Deleted"));
        setDeleteItemId(null);
      });
    });
  };

  const handlePublish = (programId: string, publish: boolean) => {
    setOpenActionId(null);
    dispatch(publishProgram({ programId, publish }))
      .unwrap()
      .then(() => {
        dispatch(fetchPrograms({ search: "", limit: itemsPerPage })).then(() => {
          toast.success(
            publish
              ? t("adminPrograms.actions.publishSuccess", "Program Published")
              : t("adminPrograms.actions.unpublishSuccess", "Program Unpublished")
          );
        });
      })
      .catch(() => {});
  };

  return (
    <div className="w-full flex-1 flex flex-col justify-between overflow-hidden">
      {/* Table Scrollable Container */}
      <div className="w-full overflow-x-auto flex-1 custom-scrollbar">
        <table className="w-full min-w-[850px] table-auto text-left border-collapse">
          <thead className="sticky top-0 bg-white z-10 shadow-xs">
            <tr className="bg-[#F8F9FA] text-left border-b-2 border-gray-100">
              {TABLE_HEAD.map((head) => (
                <th
                  key={head.key}
                  className={`py-3.5 px-6 font-bold text-[#7E8299] uppercase text-xs whitespace-nowrap select-none ${
                    head.sortable ? "cursor-pointer hover:text-[#037897] transition-colors" : ""
                  }`}
                  onClick={() => handleSort(head.key, head.sortable)}
                >
                  <div className="flex items-center gap-1.5">
                    <Typography
                      variant="small"
                      className="font-semibold leading-none text-[#7E8299] opacity-70 whitespace-nowrap text-xs uppercase"
                    >
                      {head.label}
                    </Typography>
                    {head.sortable && (
                      <span className="text-[#7E8299] transition-opacity shrink-0">
                        {sortConfig.column === mapColumnToCamelCase(head.key) ? (
                          sortConfig.sortOrder === "asc" ? (
                            <svg className="w-3.5 h-3.5 text-[#037897]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 15l7-7 7 7" />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5 text-[#037897]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                            </svg>
                          )
                        ) : (
                          <svg className="w-3 h-3 opacity-30 text-[#7E8299]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                          </svg>
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 bg-white text-sm">
            {programsList.map((program: AllPrograms) => {
              const isMenuOpen = openActionId === program.id;
              const formattedDate = program.startDate
                ? moment(program.startDate).format("DD MMM, YYYY")
                : "-";

              return (
                <tr
                  key={program.id}
                  className="hover:bg-amber-50/20 transition-colors group"
                >
                  {/* Icon */}
                  <td className="py-3 px-6 whitespace-nowrap w-12">
                    <div className="w-10 h-10 rounded-xl overflow-hidden bg-gray-100 border border-gray-200/80 shrink-0 shadow-2xs">
                      <img
                        src={program.icon || "/images/innerb.jpg"}
                        alt={program.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = "/images/innerb.jpg";
                        }}
                      />
                    </div>
                  </td>

                  {/* Title */}
                  <td className="py-3 px-6 max-w-xs">
                    <Link
                      to={`edit/${program.id}`}
                      className="font-bold text-[#1E293B] hover:text-[#037897] transition-colors line-clamp-1 text-sm block"
                      title={program.title}
                    >
                      {program.title}
                    </Link>
                  </td>

                  {/* Status Badge */}
                  <td className="py-3 px-6 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${
                        program.publish
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          program.publish ? "bg-emerald-500" : "bg-amber-500"
                        }`}
                      />
                      {program.publish
                        ? t("adminPrograms.status.published", "Published")
                        : t("adminPrograms.status.draft", "Draft")}
                    </span>
                  </td>

                  {/* Total Counsellings */}
                  <td className="py-3 px-6 whitespace-nowrap">
                    <span className="text-gray-700 font-medium">
                      {program.hasCounselling !== false
                        ? `${program.totalCounsellings || 0} slots`
                        : "-"}
                    </span>
                  </td>

                  {/* Booked Slots */}
                  <td className="py-3 px-6 whitespace-nowrap">
                    <span className="text-gray-700 font-medium">
                      {program.maxParticipants || 0}
                    </span>
                  </td>

                  {/* Start Date */}
                  <td className="py-3 px-6 whitespace-nowrap text-gray-600 font-medium text-xs">
                    {formattedDate}
                  </td>

                  {/* Cost */}
                  <td className="py-3 px-6 whitespace-nowrap">
                    <span className="font-bold text-[#1E293B]">
                      {!program.cost || program.cost === "0" || Number(program.cost) === 0
                        ? "Free"
                        : `$${program.cost}`}
                    </span>
                  </td>

                  {/* Action Menu (Click Triggered) */}
                  <td className="py-3 px-6 whitespace-nowrap text-right relative">
                    <div className="relative inline-block text-left" ref={isMenuOpen ? dropdownRef : undefined}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenActionId(isMenuOpen ? null : program.id);
                        }}
                        className={`p-2 rounded-xl border transition-all duration-150 flex items-center justify-center ${
                          isMenuOpen
                            ? "bg-[#037897] text-white border-[#037897] shadow-xs"
                            : "bg-white hover:bg-gray-100 text-gray-600 border-gray-200/80"
                        }`}
                        aria-label="Actions"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="5" r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>

                      {/* Dropdown Menu */}
                      {isMenuOpen && (
                        <div className="absolute right-0 mt-1.5 w-40 rounded-xl bg-white shadow-xl border border-gray-100 p-1.5 z-50 animate-in fade-in zoom-in-95 duration-150">
                          {/* Publish/Unpublish */}
                          <button
                            type="button"
                            onClick={() => handlePublish(program.id, !program.publish)}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-gray-700 hover:text-[#037897] hover:bg-[#F0F9FB] rounded-lg transition-colors text-left"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {program.publish ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              )}
                            </svg>
                            <span>
                              {program.publish
                                ? t("adminPrograms.actions.unpublish", "Unpublish")
                                : t("adminPrograms.actions.publish", "Publish")}
                            </span>
                          </button>

                          {/* Edit */}
                          <Link
                            to={`edit/${program.id}`}
                            onClick={() => setOpenActionId(null)}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-gray-700 hover:text-[#037897] hover:bg-[#F0F9FB] rounded-lg transition-colors text-left"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>{t("adminPrograms.actions.edit", "Edit")}</span>
                          </Link>

                          <div className="my-1 border-t border-gray-100" />

                          {/* Remove */}
                          <button
                            type="button"
                            onClick={() => {
                              setOpenActionId(null);
                              setDeleteItemId(program.id);
                            }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-lg transition-colors text-left"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>{t("adminPrograms.actions.remove", "Remove")}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteItemId && (
        <DeleteConfirmation
          onConfirm={() => handleDelete(deleteItemId)}
          onCancel={() => setDeleteItemId(null)}
          message={
            programsList.find((p) => p.id === deleteItemId)?.title ||
            "this program"
          }
        />
      )}

      {/* Pagination Footer */}
      <div className="flex items-center justify-between border-t border-gray-200/80 px-4 sm:px-6 py-3.5 bg-white gap-2">
        <span className="text-xs text-gray-500 font-medium">
          Showing page <span className="font-bold text-gray-800">{currentPage}</span> of{" "}
          <span className="font-bold text-gray-800">{parsedData?.totalPages || 1}</span>
        </span>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePreviousClick}
            disabled={currentPage === 1}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1 ${
              currentPage === 1
                ? "text-gray-300 border-gray-200 cursor-not-allowed bg-gray-50"
                : "text-gray-700 border-gray-300 hover:bg-gray-50 active:scale-98"
            }`}
          >
            ← {t("adminPrograms.pagination.previous", "Previous")}
          </button>

          <div className="flex items-center justify-center min-w-[32px] h-7 px-2 text-xs font-bold text-[#037897] bg-[#EBF7F9] border border-[#037897]/20 rounded-lg select-none">
            {currentPage}
          </div>

          <button
            type="button"
            onClick={handleNextClick}
            disabled={currentPage >= (parsedData?.totalPages || 1)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1 ${
              currentPage >= (parsedData?.totalPages || 1)
                ? "text-gray-300 border-gray-200 cursor-not-allowed bg-gray-50"
                : "text-gray-700 border-gray-300 hover:bg-gray-50 active:scale-98"
            }`}
          >
            {t("adminPrograms.pagination.next", "Next")} →
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListView;
